import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  applyTheme, DEFAULT_THEME, loadTheme, saveTheme, syncThemeColor,
  type Mode, type Skin, type ThemeState,
} from './theme';

interface Ctx extends ThemeState {
  setSkin(s: Skin): void;
  setMode(m: Mode): void;
}

const ThemeCtx = createContext<Ctx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // 初值同步读，**不要放在 effect 里** —— 放 effect 里会先用默认皮肤画一帧，
  // 再闪一下换成用户选的那套。
  const [theme, setTheme] = useState<ThemeState>(() =>
    loadTheme(typeof localStorage === 'undefined' ? null : localStorage));

  useEffect(() => {
    applyTheme(document.documentElement, theme);
    syncThemeColor(document);
    saveTheme(typeof localStorage === 'undefined' ? null : localStorage, theme);
  }, [theme]);

  // mode = system 时，系统深浅切换要跟着变（用户在 iOS 里按了日落自动切换）
  useEffect(() => {
    if (theme.mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => syncThemeColor(document);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme.mode]);

  const value = useMemo<Ctx>(() => ({
    ...theme,
    setSkin: (skin: Skin) => setTheme((t) => ({ ...t, skin })),
    setMode: (mode: Mode) => setTheme((t) => ({ ...t, mode })),
  }), [theme]);

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): Ctx {
  const c = useContext(ThemeCtx);
  if (!c) throw new Error('useTheme 必须在 ThemeProvider 里用');
  return c;
}

export { DEFAULT_THEME };
